<script setup>
/**
 * CartDrawer Component
 * Cart drawer (desktop) / fullscreen modal (mobile)
 * Opens from LEFT side
 */
import { ref, computed, watch, toRef, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useBackButtonClose } from '../../../core/composables/useBackButtonClose.js'
import { useCartStore } from '../../../app/store/cart.js'
import { useCatalogStore } from '../../../app/store/catalog.js'
import { useAuthStore } from '../../../app/store/index.js'
import { usePromotionsStore } from '../../../app/store/promotions.js'
import { useAmountDiscountsStore } from '../../../app/store/amountDiscounts.js'
import { ROUTES } from '../../../core/config/constants.js'
import { getPromoMakats } from '../../../core/utils/promoCalc.js'
import { getAllMakatsWithParitMerakez } from '../../../core/utils/discountCalc.js'
import { formatPrice } from '@core/utils/formatting.js'
import { useConfirmDialog } from '@core/composables/useConfirmDialog.js'
import CartLineRow from './CartLineRow.vue'
import CartPromoGroupCard from './CartPromoGroupCard.vue'
import CartDepositRow from './CartDepositRow.vue'
import CartFinalizeModal from './CartFinalizeModal.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const router = useRouter()
const route = useRoute()
const cartStore = useCartStore()
const catalogStore = useCatalogStore()
const authStore = useAuthStore()
const promotionsStore = usePromotionsStore()
const amountDiscountsStore = useAmountDiscountsStore()
const { confirm } = useConfirmDialog()

// Error modal state
const showPriceErrorModal = ref(false)
const showNoClientModal = ref(false)
const showMinOrderModal = ref(false)
const showFinalizeModal = ref(false)

// The immersive catalog overlay sits at z-index 3000 (teleported to body);
// when the cart is opened from there (its floating cart button), the drawer
// and every modal it spawns must layer above that overlay.
const overImmersive = computed(
  () => route.name === ROUTES.CATALOG_IMMERSIVE || route.name === ROUTES.AGENTS_CATALOG_IMMERSIVE
)

// Computed
const isOpen = computed(() => props.modelValue)
const isEmpty = computed(() => cartStore.isEmpty)
const uniqueCount = computed(() => cartStore.uniqueCount)
const isRestoring = computed(() => cartStore.isRestoring)

// Use cartRenderBlocks for unified display (includes promoGroup blocks when over-max)
const cartBlocks = computed(() => cartStore.cartRenderBlocks)

// Use pricedTotals for aggregated values
const pricedTotals = computed(() => cartStore.pricedTotals)
const subtotal = computed(() => pricedTotals.value.itemsSubtotalPreVat)
const promoSavings = computed(() => pricedTotals.value.promoSavingsPreVat)
const hasPromoSavings = computed(() => promoSavings.value > 0)

// Deposit summary
const depositSummary = computed(() => cartStore.depositSummary)

// Check if any line has missing price (use raw lines)
const hasLineMissingPrice = computed(() => {
  return cartStore.linesArray.some((line) => {
    const item = catalogStore.itemsByMakat.get(line.makat)
    return !item?.price
  })
})

// Check if user has a client assigned
const hasClientAssigned = computed(() => {
  return !!authStore.user?.profile?.client_makat
})

// Rosman client = the logged-in user's linked client whose assigned agent is not '10'.
// Reads from already-loaded auth store data (no Firestore call).
// Agent / impersonation flow is unchanged — gate stays off when an agent is acting.
const isRosmanClient = computed(() => {
  if (authStore.isAgent || authStore.isImpersonatingAgent) return false
  const c = authStore.user?.client
  if (!c) return false
  const agent = String(c.agent ?? '').trim()
  return agent !== '' && agent !== '10'
})

const continueButtonLabel = computed(() => (isRosmanClient.value ? 'סיום הזמנה' : 'המשך להזמנה'))

// Resolve the client doc relevant to this checkout:
// - Regular client checking out -> their own client doc on the auth store
// - Agent / impersonating admin placing on behalf -> the linkedClient on the catalog store
const targetClientDoc = computed(() => {
  return authStore.user?.client || catalogStore.linkedClient || null
})

// Minimum order amount (pre-VAT) configured on the client doc in ClientsV2
const clientMinOrder = computed(() => {
  const raw = targetClientDoc.value?.min_order
  const num = Number(raw)
  return Number.isFinite(num) && num > 0 ? num : 0
})

// Cart total used for the min-order comparison (pre-VAT, post-promo)
const cartTotalForMinOrder = computed(() => pricedTotals.value.totalPreVat || 0)

// True when a minimum is configured and the current cart is below it
const cartBelowMinimumOrder = computed(() => {
  if (clientMinOrder.value <= 0) return false
  return cartTotalForMinOrder.value < clientMinOrder.value
})

// How much more is needed to meet the minimum (for the modal copy)
const minOrderShortfall = computed(() => {
  return Math.max(0, clientMinOrder.value - cartTotalForMinOrder.value)
})

// Find the first cart block makat that has additional products (for tutorial targeting)
const firstAdditionalProductsMakat = computed(() => {
  for (const block of cartBlocks.value) {
    if (block.type === 'item' && block.group) {
      const info = getAdditionalProductsInfo(block.group)
      if (info) return block.group.makat
    }
  }
  return null
})

// Close drawer
function close() {
  emit('update:modelValue', false)
}

// Intercept back button to close cart drawer instead of navigating
const { neutralizeHistory } = useBackButtonClose(toRef(props, 'modelValue'), close)

// Handle line actions
function handleInc(makat) {
  const item = catalogStore.itemsByMakat.get(makat)
  if (!item?.price) {
    showPriceErrorModal.value = true
    return
  }
  cartStore.inc(makat)
}

function handleDec(makat) {
  cartStore.dec(makat)
}

function handleSetQty(makat, qty) {
  const item = catalogStore.itemsByMakat.get(makat)
  if (!item?.price) {
    showPriceErrorModal.value = true
    return
  }
  cartStore.setQty(makat, qty)
}

function handleRemove(makat) {
  cartStore.removeItem(makat)
}

// Clear all
async function handleClearCart() {
  const ok = await confirm({
    title: 'ריקון עגלה',
    message: 'האם למחוק את כל הפריטים מהעגלה?',
    confirmText: 'מחק הכל',
    variant: 'danger',
  })
  if (ok) cartStore.clearCart()
}

// Check if a cart group has additional products in its promo/discount
function getAdditionalProductsInfo(group) {
  if (group.promoGroupId) {
    const promo = promotionsStore.getById(group.promoGroupId)
    if (promo) {
      const makats = getPromoMakats(promo)
      if (makats.length > 1) {
        return { type: 'promo', targetId: group.promoGroupId }
      }
    }
  }

  const paritMerakez = group.discountInfo?.paritMerakez || group.item?.paritMerakez
  if (paritMerakez && amountDiscountsStore.hasAmountDiscount(paritMerakez)) {
    const makats = getAllMakatsWithParitMerakez(paritMerakez, catalogStore.itemsByMakat)
    if (makats.length > 1) {
      return { type: 'discount', targetId: String(paritMerakez) }
    }
  }

  return null
}

// Navigate to promotion detail or quantity discount view
function handleViewAdditionalProducts(info) {
  close()
  const clientMakat = route.params.clientMakat

  if (info.type === 'promo') {
    router.push({
      name: clientMakat ? ROUTES.AGENTS_PROMOTION_DETAIL : ROUTES.PROMOTION_DETAIL,
      params: clientMakat ? { clientMakat, promoId: info.targetId } : { promoId: info.targetId },
    })
  } else if (info.type === 'discount') {
    router.push({
      name: clientMakat ? ROUTES.AGENTS_QUANTITY_DISCOUNT : ROUTES.QUANTITY_DISCOUNT,
      params: clientMakat
        ? { clientMakat, paritMerakez: info.targetId }
        : { paritMerakez: info.targetId },
    })
  }
}

// Continue to checkout
function handleContinue() {
  if (!hasClientAssigned.value) {
    showNoClientModal.value = true
    return
  }
  if (hasLineMissingPrice.value) {
    showPriceErrorModal.value = true
    return
  }
  if (cartBelowMinimumOrder.value) {
    showMinOrderModal.value = true
    return
  }
  if (isRosmanClient.value) {
    showFinalizeModal.value = true
    return
  }
  goToCheckout()
}

function goToCheckout(query = undefined) {
  // Neutralize the back-button history entry before navigating,
  // otherwise history.back() races with router.push() and undoes the navigation
  neutralizeHistory()
  close()
  router.push({ name: ROUTES.CHECKOUT, query })
}

function handleFinalizeCredit() {
  showFinalizeModal.value = false
  goToCheckout()
}

function handleFinalizeSendOrder() {
  showFinalizeModal.value = false
  goToCheckout({ mode: 'pre-sale' })
}

// Close error modals
function closePriceErrorModal() {
  showPriceErrorModal.value = false
}

function closeNoClientModal() {
  showNoClientModal.value = false
}

function closeMinOrderModal() {
  showMinOrderModal.value = false
}

// Prevent body scroll when open
watch(isOpen, (open) => {
  if (open) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Cleanup on unmount
onUnmounted(() => {
  document.body.style.overflow = ''
})

// Handle escape key
function handleKeydown(e) {
  if (e.key === 'Escape' && isOpen.value) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <Teleport to="body">
    <!-- Overlay -->
    <Transition name="overlay">
      <div
        v-if="isOpen"
        class="drawer-overlay"
        :class="{ 'over-immersive': overImmersive }"
        @click="close"
      ></div>
    </Transition>

    <!-- Drawer -->
    <Transition name="drawer">
      <div
        v-if="isOpen"
        class="cart-drawer"
        :class="{ 'over-immersive': overImmersive }"
        role="dialog"
        aria-modal="true"
        aria-label="עגלת קניות"
      >
        <!-- Header -->
        <header class="drawer-header">
          <h2 class="drawer-title">
            <svg
              class="title-icon"
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
            </svg>
            <span>עגלת קניות</span>
            <span v-if="uniqueCount > 0" class="item-count">({{ uniqueCount }})</span>
          </h2>
          <button class="close-btn" aria-label="סגור" @click="close">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <!-- Content -->
        <div class="drawer-content">
          <!-- Loading State (restoring cart from Firestore) -->
          <div v-if="isRestoring" class="restoring-state">
            <div class="restoring-spinner"></div>
            <p class="restoring-text">טוען עגלה...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="isEmpty" class="empty-state">
            <div class="empty-icon">
              <svg
                viewBox="0 0 24 24"
                width="64"
                height="64"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
              </svg>
            </div>
            <p class="empty-text">העגלה ריקה</p>
            <p class="empty-subtext">הוסף פריטים מהקטלוג כדי להתחיל</p>
          </div>

          <!-- Cart Lines (render blocks) -->
          <div
            v-else
            v-tutorial="{
              id: 'cart-items-list',
              text: 'כאן מוצגים כל הפריטים שהוספתם לעגלה',
              importance: 'info',
              showOnce: true,
              autoTrigger: true,
              group: 'cart-intro',
              step: 1,
            }"
            class="cart-lines"
          >
            <template v-for="(block, blockIndex) in cartBlocks" :key="block.displayKey">
              <!-- Promo Group Card (when promo max exceeded) -->
              <CartPromoGroupCard
                v-if="block.type === 'promoGroup'"
                :block="block"
                @inc="handleInc"
                @dec="handleDec"
                @set-qty="handleSetQty"
                @remove="handleRemove"
                @view-additional-products="handleViewAdditionalProducts"
              />
              <!-- Regular Item Card -->
              <CartLineRow
                v-else
                :group="block.group"
                :additional-products-info="getAdditionalProductsInfo(block.group)"
                :show-additional-tutorial="block.group?.makat === firstAdditionalProductsMakat"
                :tutorial-config="
                  blockIndex === 0
                    ? {
                        id: 'cart-line-controls',
                        text: 'השתמשו ב +/- לעדכון כמויות או לחצו על המספר לעריכה ישירה',
                        importance: 'info',
                        showOnce: true,
                        autoTrigger: false,
                        group: 'cart-intro',
                        step: 2,
                      }
                    : null
                "
                @inc="handleInc"
                @dec="handleDec"
                @set-qty="handleSetQty"
                @remove="handleRemove"
                @view-additional-products="handleViewAdditionalProducts"
              />
            </template>

            <!-- Deposit Card (if deposit > 0) -->
            <CartDepositRow
              v-if="depositSummary.hasDeposit"
              :deposit-pre-vat="depositSummary.depositPreVat"
              :deposit-units="depositSummary.depositUnits"
            />
          </div>
        </div>

        <!-- Footer -->
        <footer v-if="!isEmpty && !isRestoring" class="drawer-footer">
          <!-- Clear Cart -->
          <button class="clear-btn" @click="handleClearCart">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            <span>נקה עגלה</span>
          </button>

          <!-- Promo Savings -->
          <div v-if="hasPromoSavings" class="savings-row">
            <span class="savings-label">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                />
              </svg>
              חיסכון במבצעים
            </span>
            <div class="savings-value">
              <span class="savings-amount">-{{ formatPrice(promoSavings) }}</span>
              <span class="savings-currency">₪</span>
            </div>
          </div>

          <!-- Deposit Total (if deposit > 0) - moved above subtotal -->
          <div v-if="depositSummary.hasDeposit" class="deposit-total-row">
            <span class="deposit-total-label">סה"כ פיקדון</span>
            <div class="deposit-total-value">
              <span class="deposit-total-amount">{{
                formatPrice(depositSummary.depositPreVat)
              }}</span>
              <span class="deposit-total-currency">₪</span>
            </div>
          </div>

          <!-- Subtotal (pre-VAT) -->
          <div
            v-tutorial="{
              id: 'cart-totals',
              text: 'סיכום ההזמנה: סכום ביניים, הנחות וסה״כ לתשלום',
              importance: 'important',
              showOnce: true,
              autoTrigger: false,
              group: 'cart-intro',
              step: 3,
            }"
            class="subtotal-row"
          >
            <span class="subtotal-label">סה"כ לפני מע"מ</span>
            <div class="subtotal-value">
              <span class="subtotal-amount">{{ formatPrice(pricedTotals.totalPreVat) }}</span>
              <span class="subtotal-currency">₪</span>
            </div>
          </div>

          <!-- Grand Total (with VAT including deposit) -->
          <div class="grand-total-row">
            <span class="grand-total-label"
              >מחיר כולל מע"מ{{ depositSummary.hasDeposit ? ' כולל פיקדון' : '' }}</span
            >
            <div class="grand-total-value">
              <span class="grand-total-amount">{{ formatPrice(pricedTotals.totalWithVat) }}</span>
              <span class="grand-total-currency">₪</span>
            </div>
          </div>

          <!-- Continue Button -->
          <button
            v-tutorial="{
              id: 'cart-continue-btn',
              text: 'לחצו להמשך לתהליך ההזמנה',
              importance: 'important',
              showOnce: true,
              autoTrigger: false,
              group: 'cart-intro',
              step: 4,
            }"
            class="continue-btn"
            :disabled="hasLineMissingPrice || !hasClientAssigned"
            @click="handleContinue"
          >
            <span>{{ continueButtonLabel }}</span>
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        </footer>
      </div>
    </Transition>

    <!-- Price Error Modal -->
    <Transition name="modal">
      <div
        v-if="showPriceErrorModal"
        class="error-modal-overlay"
        :class="{ 'over-immersive': overImmersive }"
        @click="closePriceErrorModal"
      >
        <div class="error-modal" @click.stop>
          <div class="error-modal-icon">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 class="error-modal-title">שגיאה</h3>
          <p class="error-modal-text">
            לא ניתן להוסיף פריט זה לעגלה כי לא נמצא לו מחיר. אנא פנה לתמיכה.
          </p>
          <button class="error-modal-btn" @click="closePriceErrorModal">הבנתי</button>
        </div>
      </div>
    </Transition>

    <!-- No Client Modal -->
    <Transition name="modal">
      <div
        v-if="showNoClientModal"
        class="error-modal-overlay"
        :class="{ 'over-immersive': overImmersive }"
        @click="closeNoClientModal"
      >
        <div class="error-modal" @click.stop>
          <div class="error-modal-icon warning">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
              <line x1="18" y1="8" x2="23" y2="13" />
              <line x1="23" y1="8" x2="18" y2="13" />
            </svg>
          </div>
          <h3 class="error-modal-title">לא משויך לקוח</h3>
          <p class="error-modal-text">
            לא ניתן לבצע הזמנה ללא שיוך לחשבון לקוח. אנא פנה למנהל המערכת.
          </p>
          <button class="error-modal-btn" @click="closeNoClientModal">הבנתי</button>
        </div>
      </div>
    </Transition>

    <!-- Finalize Order Modal (Rosman clients) -->
    <CartFinalizeModal
      v-model="showFinalizeModal"
      :elevated="overImmersive"
      @pay-credit="handleFinalizeCredit"
      @send-order="handleFinalizeSendOrder"
    />

    <!-- Minimum Order Modal -->
    <Transition name="modal">
      <div
        v-if="showMinOrderModal"
        class="error-modal-overlay"
        :class="{ 'over-immersive': overImmersive }"
        @click="closeMinOrderModal"
      >
        <div class="error-modal min-order-modal" @click.stop>
          <div class="error-modal-icon warning">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
            </svg>
          </div>
          <h3 class="error-modal-title">לא ניתן להמשיך להזמנה</h3>
          <p class="error-modal-text">
            סכום ההזמנה נמוך ממינימום ההזמנה שנקבע ללקוח.
            <br />
            יש להוסיף פריטים נוספים לעגלה כדי להמשיך.
          </p>

          <div class="min-order-breakdown">
            <div class="min-order-row">
              <span class="min-order-label">מינימום הזמנה (לפני מע"מ)</span>
              <span class="min-order-value">{{ formatPrice(clientMinOrder) }} ₪</span>
            </div>
            <div class="min-order-row">
              <span class="min-order-label">סכום נוכחי בעגלה</span>
              <span class="min-order-value">{{ formatPrice(cartTotalForMinOrder) }} ₪</span>
            </div>
            <div class="min-order-row min-order-row-shortfall">
              <span class="min-order-label">חסר להשלמה</span>
              <span class="min-order-value">{{ formatPrice(minOrderShortfall) }} ₪</span>
            </div>
          </div>

          <button class="error-modal-btn" @click="closeMinOrderModal">חזרה לקטלוג</button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ============================================
   Overlay
   ============================================ */
.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

/* Opened from the immersive catalog (overlay at z 3000) — layer above it */
.drawer-overlay.over-immersive {
  z-index: 3050;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* ============================================
   Drawer
   ============================================ */
.cart-drawer {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1002;

  width: 420px;
  max-width: 100%;

  display: flex;
  flex-direction: column;

  background: var(--bg-primary);
  box-shadow: var(--shadow-xl);
}

.cart-drawer.over-immersive {
  z-index: 3051;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(-100%);
}

/* Header */
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--gradient-header);
  flex-shrink: 0;
}

.drawer-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: white;
}

.title-icon {
  flex-shrink: 0;
}

.item-count {
  font-weight: var(--font-weight-normal);
  opacity: 0.8;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Content */
.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
}

/* Restoring State (loading cart from Firestore) */
.restoring-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-3xl) var(--spacing-lg);
  text-align: center;
}

.restoring-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: var(--spacing-md);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.restoring-text {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-3xl) var(--spacing-lg);
  text-align: center;
}

.empty-icon {
  color: var(--text-muted);
  margin-bottom: var(--spacing-lg);
  opacity: 0.5;
}

.empty-text {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.empty-subtext {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* Cart Lines */
.cart-lines {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

/* Footer */
.drawer-footer {
  flex-shrink: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  font-size: var(--font-size-xs);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  align-self: flex-start;
}

.clear-btn:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
}

/* Promo Savings Row */
.savings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: rgba(34, 197, 94, 0.1);
  border-radius: var(--border-radius-sm);
}

.savings-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: #15803d;
  font-weight: var(--font-weight-medium);
}

.savings-label svg {
  color: #22c55e;
}

.savings-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.savings-amount {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: #15803d;
}

.savings-currency {
  font-size: var(--font-size-xs);
  color: #15803d;
}

.subtotal-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) 0;
  border-top: 1px solid var(--border-color);
}

.subtotal-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.subtotal-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.subtotal-amount {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.subtotal-currency {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* Deposit Total Row */
.deposit-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) 0;
}

.deposit-total-label {
  font-size: var(--font-size-sm);
  color: #7c3aed;
  font-weight: var(--font-weight-medium);
}

.deposit-total-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.deposit-total-amount {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #7c3aed;
}

.deposit-total-currency {
  font-size: var(--font-size-xs);
  color: #7c3aed;
}

/* Grand Total Row (with VAT) */
.grand-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) 0;
  border-top: 2px solid var(--color-primary);
}

.grand-total-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.grand-total-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.grand-total-amount {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.grand-total-currency {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
}

.continue-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-xs);
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.continue-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

.continue-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============================================
   Error Modal
   ============================================ */
.error-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

.error-modal-overlay.over-immersive {
  z-index: 3060;
}

.error-modal {
  width: 100%;
  max-width: 360px;
  padding: var(--spacing-xl);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  text-align: center;
}

.error-modal-icon {
  color: var(--color-error);
  margin-bottom: var(--spacing-md);
}

.error-modal-icon.warning {
  color: #f59e0b;
}

.error-modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm);
}

.error-modal-text {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 var(--spacing-lg);
}

.error-modal-btn {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.error-modal-btn:hover {
  background: var(--color-primary-hover);
}

.min-order-modal {
  max-width: 400px;
}

.min-order-breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  margin: 0 0 var(--spacing-lg);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  text-align: right;
}

.min-order-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.min-order-row .min-order-value {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.min-order-row-shortfall {
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-sm);
  border-top: 1px dashed var(--border-color);
}

.min-order-row-shortfall .min-order-label {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.min-order-row-shortfall .min-order-value {
  font-size: var(--font-size-base);
  color: var(--color-error);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .error-modal,
.modal-leave-active .error-modal {
  transition: transform 0.2s ease;
}

.modal-enter-from .error-modal,
.modal-leave-to .error-modal {
  transform: scale(0.95);
}

/* ============================================
   Mobile: Fullscreen
   ============================================ */
@media (max-width: 767px) {
  .cart-drawer {
    width: 100%;
    max-width: 100%;
  }

  .drawer-header {
    padding: var(--spacing-md);
  }

  .drawer-content {
    padding: var(--spacing-sm);
  }

  .drawer-footer {
    padding: var(--spacing-md);
  }
}
</style>
